import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Textarea from '@amzn/meridian/textarea';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Tooltip from '@amzn/meridian/tooltip';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import HoverZoomImage from './HoverZoomImage';
import Icon from '@amzn/meridian/icon';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import bulbTokens from '@amzn/meridian-tokens/base/icon/bulb';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import { parseItemIds } from '@components/agile/utils/parseItemIds';
import {
  timestampToDateStr,
  dateStrToTimestamp,
  isIndefinite,
  isBeforeDate,
  getEarliestStartDateStr,
  isOutsideDateRange,
} from '@components/agile/utils/dateHelpers';
import { ItemType, ApplyItemsChangeInput, MapError, ItemMetaDataEntry, ItemEntry } from '@graphql/generated/graphql';
import useGetItemMetaData from '@store/hooks/useGetItemMetaData';
import useGetSubcategoryRecommendation from '@store/hooks/useGetSubcategoryRecommendation';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import AiSuggestionBadge from '@components/agile/aiRecommendation/AiSuggestionBadge';
import AiSparkleIcon from '@components/agile/aiRecommendation/AiSparkleIcon';
import {
  clearAiMetadata,
  applyRecommendations,
  getItemsWithoutSubcategory,
  isAiSuggestEnabled,
} from '@components/agile/aiRecommendation/aiRecommendationUtils';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';
import { toItemType } from 'src/utils/itemTypeUtils';

// --- StagedItem interface ---

export interface StagedItem {
  itemId: string;
  itemType: ItemType;
  subcategoryId: string | undefined;
  startTime: number;
  endTime: number;
  title?: string;
  image?: string;
  recommendedBy?: 'AI';
  reasonCode?: 'HISTORICAL_ASSIGNMENT' | 'TITLE_MATCH' | 'DEFAULT_ASSIGNMENT';
  reasonDetail?: string;
}

// --- Helper functions ---

export function createStagedItems(
  parsedIds: string[],
  itemType: ItemType,
  startTime: number,
  endTime: number,
): StagedItem[] {
  return parsedIds.map((id) => ({
    itemId: id,
    itemType,
    subcategoryId: undefined,
    startTime,
    endTime,
  }));
}

export function enrichStagedItemsWithMetadata(items: StagedItem[], metadata: ItemMetaDataEntry[]): StagedItem[] {
  const metadataMap = new Map<string, ItemMetaDataEntry>();
  for (const entry of metadata) {
    metadataMap.set(entry.itemId, entry);
  }
  return items.map((item) => {
    const meta = metadataMap.get(item.itemId);
    return {
      ...item,
      title: meta?.title ?? item.title,
      image: meta?.image ?? item.image,
    };
  });
}

/**
 * Save is enabled when there are items, and (for subcategory-scoped programs)
 * every staged item has a subcategoryId selected. Category-scoped programs
 * (e.g. GFC) bind items at the macroplanogram level, so subcategory presence
 * is not a save precondition.
 */
export function isSaveEnabled(items: StagedItem[], subcategoryRequired = true): boolean {
  if (items.length === 0) return false;
  if (!subcategoryRequired) return true;
  return items.every((item) => item.subcategoryId !== undefined && item.subcategoryId !== '');
}

export function buildMutationInput(
  items: StagedItem[],
  storeId: string,
  macroplanogramId: string,
  /** Macroplanogram-level categoryId (Phase 1 GFC). When supplied, each
   *  emitted entry carries this categoryId; subcategoryId is left undefined.
   *  When omitted, entries fall back to their staged subcategoryId. */
  categoryId?: string,
): ApplyItemsChangeInput {
  return {
    storeId,
    macroplanogramId,
    newEntries: items.map((item) => ({
      itemId: item.itemId,
      itemType: item.itemType,
      subcategoryId: item.subcategoryId,
      categoryId,
      startTime: item.startTime,
      endTime: item.endTime,
    })),
  };
}

export function mapSaveErrors(errors: MapError[], subcategoryMap?: Map<string, string>): Map<string, string> {
  const errorMap = new Map<string, string>();
  for (const err of errors) {
    let message = err.error.errorMessage;
    // Replace subcategory IDs with names if map is provided
    if (subcategoryMap) {
      subcategoryMap.forEach((name, id) => {
        message = message.replaceAll(`'${id}'`, `'${name}'`);
      });
    }
    errorMap.set(err.key, message);
  }
  return errorMap;
}

export function findDuplicates(parsedIds: string[], itemType: ItemType, existingItems: ItemEntry[]): string[] {
  const existingSet = new Set(
    existingItems.filter((entry) => entry.itemType === itemType).map((entry) => entry.itemId),
  );
  return parsedIds.filter((id) => existingSet.has(id));
}

// --- AI Recommendation helper functions (imported from shared module) ---
// applyRecommendations, getItemsWithoutSubcategory, isAiSuggestEnabled
// are imported from '@components/agile/aiRecommendation/aiRecommendationUtils'

// --- Component Props ---

export interface AddItemInputProps {
  storeId: string;
  macroplanogramId: string;
  selectedDate: string;
  macroplanogramStartTime: number;
  macroplanogramEndTime: number;
  subcategories: storeSubcategory[];
  existingItems: ItemEntry[] | undefined;
  stagedItems: StagedItem[];
  onStagedItemsChange: (items: StagedItem[]) => void;
  /** Optional per-item save errors to display inline (managed by parent modal). */
  saveErrors?: Map<string, string>;
}

// --- Item type label ---

/**
 * @deprecated Use `useProgramConfig().labels.itemIdentifierType`
 */
export const SCANCODE_LABEL = 'Scancode';

// --- AddItemInput Component ---

const AddItemInput: React.FC<AddItemInputProps> = (props) => {
  const {
    storeId,
    selectedDate,
    macroplanogramStartTime,
    macroplanogramEndTime,
    subcategories,
    existingItems,
    stagedItems,
    onStagedItemsChange,
    saveErrors = new Map(),
  } = props;

  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const categoryLabel = config.labels.category;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const scopeColumnLabel = scope === 'subcategory' ? subcategoryLabel : categoryLabel;
  const itemType = toItemType(itemIdentifierLabel);

  // Feature flag for AI subcategory suggestion
  const isAiFeatureEnabled = useFeatureFlag(FeatureName.AI_SUBCATEGORY_SUGGESTION);

  // AI recommendation error state for inline alert
  const [aiError, setAiError] = useState<string | null>(null);

  // Memoize item IDs without subcategory to avoid new array reference each render
  const itemIdsWithoutSubcategory = useMemo(() => getItemsWithoutSubcategory(stagedItems), [stagedItems]);

  // Valid subcategory IDs for validation
  const validSubcategoryIds = useMemo(() => new Set(subcategories.map((s) => s.subcategoryId)), [subcategories]);

  // AI recommendation hook — lazy query, only fires on button click
  const { fetchRecommendations, isLoading: isAiLoading } = useGetSubcategoryRecommendation({
    storeId,
    macroplanogramId: props.macroplanogramId,
    itemIds: itemIdsWithoutSubcategory,
    onSuccess: (recommendations) => {
      // Filter out any recommendations with invalid subcategoryIds
      const validRecommendations = recommendations.filter((r) => validSubcategoryIds.has(r.subcategoryId));
      onStagedItemsChange(applyRecommendations(stagedItems, validRecommendations));
      setAiError(null);
    },
    onError: (error) => {
      setAiError(error);
    },
  });

  // AI Suggest button click handler
  const handleAiSuggest = useCallback(() => {
    setAiError(null);
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Internal state
  const [rawInput, setRawInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState<string[]>([]);

  // Metadata fetch — enabled only when we have staged items
  const {
    isLoading: metadataLoading,
    isSuccess: metadataSuccess,
    items: metadataItems,
  } = useGetItemMetaData(
    {
      storeId,
      itemIds: stagedItems.map((item) => item.itemId),
      itemType,
    },
    { enabled: stagedItems.length > 0 },
  );

  // Track whether we've already enriched for the current metadata response
  const enrichedRef = useRef(false);

  // Stable key for the current set of staged item IDs
  const stagedItemKey = stagedItems.map((item) => item.itemId).join(',');

  // Reset enrichment flag when the set of staged items changes
  useEffect(() => {
    enrichedRef.current = false;
  }, [stagedItemKey]);

  // Enrich staged items with metadata when it arrives (once per fetch)
  useEffect(() => {
    if (metadataSuccess && metadataItems && metadataItems.length > 0 && !enrichedRef.current) {
      enrichedRef.current = true;
      onStagedItemsChange(enrichStagedItemsWithMetadata(stagedItems, metadataItems));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataSuccess, metadataItems]);

  // "Add to list" handler
  const handleAddToList = useCallback(() => {
    setValidationError(null);
    setDuplicateWarnings([]);

    const parsedIds = parseItemIds(rawInput);

    if (parsedIds.length === 0) {
      setValidationError('At least one item ID is required');
      return;
    }

    // Check for duplicates against existing assortment
    if (existingItems && existingItems.length > 0) {
      const dupes = findDuplicates(parsedIds, itemType, existingItems);
      if (dupes.length > 0) {
        setDuplicateWarnings(dupes);
      }
    }

    // Filter out IDs already in the staged list
    const existingStagedIds = new Set(stagedItems.map((item) => item.itemId));
    const newIds = parsedIds.filter((id) => !existingStagedIds.has(id));

    if (newIds.length === 0) {
      setValidationError('All entered items are already in the list.');
      return;
    }

    const startTime = dateStrToTimestamp(selectedDate);
    const newItems = createStagedItems(newIds, itemType, startTime, macroplanogramEndTime);
    onStagedItemsChange([...stagedItems, ...newItems]);
    setRawInput('');
  }, [rawInput, selectedDate, macroplanogramEndTime, existingItems, stagedItems, onStagedItemsChange, itemType]);

  // Remove a staged item
  const handleRemoveItem = useCallback(
    (itemId: string) => {
      onStagedItemsChange(stagedItems.filter((item) => item.itemId !== itemId));
      setDuplicateWarnings((prev) => prev.filter((id) => id !== itemId));
    },
    [stagedItems, onStagedItemsChange],
  );

  // Update start date for a staged item
  const handleStartDateChange = useCallback(
    (itemId: string, dateStr: string) => {
      const newStartTime = dateStrToTimestamp(dateStr);
      onStagedItemsChange(
        stagedItems.map((item) => (item.itemId === itemId ? { ...item, startTime: newStartTime } : item)),
      );
    },
    [stagedItems, onStagedItemsChange],
  );

  // Update end date for a staged item
  const handleEndDateChange = useCallback(
    (itemId: string, dateStr: string) => {
      const newEndTime = dateStrToTimestamp(dateStr);
      onStagedItemsChange(
        stagedItems.map((item) => (item.itemId === itemId ? { ...item, endTime: newEndTime } : item)),
      );
    },
    [stagedItems, onStagedItemsChange],
  );

  // Update subcategory for a staged item
  const handleSubcategoryChange = useCallback(
    (itemId: string, subcategoryId: string) => {
      onStagedItemsChange(
        stagedItems.map((item) => (item.itemId === itemId ? clearAiMetadata({ ...item, subcategoryId }) : item)),
      );
    },
    [stagedItems, onStagedItemsChange],
  );

  return (
    <Column spacing="400">
      {/* Input section */}
      <Column spacing="300">
        <Text type="b300">Paste {itemIdentifierLabel.toLowerCase()}s separated by commas, spaces, or newlines:</Text>
        <Textarea value={rawInput} onChange={setRawInput} placeholder="e.g. B00ABC1234, B00DEF5678" rows={4} />
        {validationError && (
          <Alert type="error" size="small">
            {validationError}
          </Alert>
        )}
      </Column>

      {/* Add to list button */}
      <Row spacing="300" alignmentVertical="bottom">
        <Button type="primary" onClick={handleAddToList}>
          Add to change list
        </Button>
      </Row>

      {/* Duplicate warning banner */}
      {duplicateWarnings.length > 0 && (
        <Alert type="warning" size="small">
          The following items already exist in the assortment: {duplicateWarnings.join(', ')}. You may still proceed.
        </Alert>
      )}

      {/* Preview table */}
      {stagedItems.length > 0 && (
        <Column spacing="300">
          <Row alignmentVertical="center" spacing="300">
            <Text type="h100">Preview ({stagedItems.length} items)</Text>
            {isAiFeatureEnabled && scope === 'subcategory' && (
              <Tooltip position="top" title="AI Suggest subcategories for all items">
                <Button
                  type="icon"
                  size="small"
                  onClick={handleAiSuggest}
                  disabled={!isAiSuggestEnabled(stagedItems) || isAiLoading}
                  aria-label="AI Suggest subcategories"
                >
                  {isAiLoading ? <Loader type="circular" size="small" /> : <AiSparkleIcon size={20} />}
                </Button>
              </Tooltip>
            )}
          </Row>

          {aiError && (
            <Alert type="error" size="small" onClose={() => setAiError(null)}>
              {aiError}
            </Alert>
          )}

          {metadataLoading && (
            <Row alignmentHorizontal="center" spacingInset="300">
              <Loader type="circular" size="small" />
              <Text type="b200">Loading metadata...</Text>
            </Row>
          )}

          {!metadataLoading && scope === 'subcategory' && subcategories.length === 0 && (
            <Alert type="warning" size="small">
              No {subcategoryLabel.toLowerCase()} available for the selected macroplanogram.
            </Alert>
          )}

          {!metadataLoading && (
            <Table headerRows={1} showDividers spacing="small">
              <TableRow backgroundColor="secondary">
                <TableCell>{itemIdentifierLabel}</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Title</TableCell>
                {scope === 'subcategory' && <TableCell>{scopeColumnLabel}</TableCell>}
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Remove</TableCell>
              </TableRow>
              {stagedItems.map((item) => (
                <TableRow key={item.itemId}>
                  <TableCell>
                    <Column>
                      <Text type="b200">{item.itemId}</Text>
                      {saveErrors.has(item.itemId) && (
                        <Text type="b100" color="error">
                          {saveErrors.get(item.itemId)}
                        </Text>
                      )}
                    </Column>
                  </TableCell>
                  <TableCell>
                    {item.image ? (
                      <HoverZoomImage src={item.image} alt={item.itemId} width={40} height={40} />
                    ) : (
                      <Text type="b200">—</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box maxWidth="200px">
                      <Text type="b200">{item.title ?? '—'}</Text>
                    </Box>
                  </TableCell>
                  {scope === 'subcategory' ? (
                    <TableCell>
                      <Row spacing="200" alignmentVertical="center">
                        <Select
                          value={item.subcategoryId ?? ''}
                          onChange={(value: string) => handleSubcategoryChange(item.itemId, value)}
                          placeholder="Select..."
                          size="small"
                          width={180}
                        >
                          {subcategories.map((sub) => (
                            <SelectOption
                              key={sub.subcategoryId}
                              value={sub.subcategoryId}
                              label={sub.subcategoryName}
                            />
                          ))}
                        </Select>
                        {item.recommendedBy === 'AI' && item.reasonDetail && (
                          <AiSuggestionBadge reasonDetail={item.reasonDetail} />
                        )}
                      </Row>
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Row spacing="none" alignmentVertical="center">
                      <DatePicker
                        value={timestampToDateStr(item.startTime)}
                        onChange={(value: string) => handleStartDateChange(item.itemId, value)}
                        size="small"
                        disabledDates={isBeforeDate(getEarliestStartDateStr(macroplanogramStartTime))}
                      />
                      {item.startTime !== dateStrToTimestamp(selectedDate) && (
                        <Button
                          type="icon"
                          size="small"
                          onClick={() =>
                            onStagedItemsChange(
                              stagedItems.map((s) =>
                                s.itemId === item.itemId ? { ...s, startTime: dateStrToTimestamp(selectedDate) } : s,
                              ),
                            )
                          }
                          aria-label="Reset start date"
                        >
                          <Icon tokens={closeKnockoutTokens} />
                        </Button>
                      )}
                    </Row>
                  </TableCell>
                  <TableCell>
                    <Row spacing="none" alignmentVertical="center">
                      <DatePicker
                        value={isIndefinite(item.endTime) ? '' : timestampToDateStr(item.endTime)}
                        onChange={(value: string) => handleEndDateChange(item.itemId, value)}
                        size="small"
                        placeholder={isIndefinite(item.endTime) ? 'Indefinite' : undefined}
                        disabledDates={isOutsideDateRange(timestampToDateStr(item.startTime), macroplanogramEndTime)}
                      />
                      {item.endTime !== macroplanogramEndTime && (
                        <Button
                          type="icon"
                          size="small"
                          onClick={() =>
                            onStagedItemsChange(
                              stagedItems.map((s) =>
                                s.itemId === item.itemId ? { ...s, endTime: macroplanogramEndTime } : s,
                              ),
                            )
                          }
                          aria-label="Reset end date"
                        >
                          <Icon tokens={closeKnockoutTokens} />
                        </Button>
                      )}
                      {item.endTime === macroplanogramEndTime && (
                        <Tooltip
                          position="top"
                          title={
                            isIndefinite(macroplanogramEndTime)
                              ? 'This zone map has no end date. The assortment end date is set to the maximum allowed value.'
                              : `This end date matches the zone map end date (${timestampToDateStr(macroplanogramEndTime)}), which is the latest allowed date for this assortment.`
                          }
                        >
                          <Icon tokens={bulbTokens} />
                        </Tooltip>
                      )}
                    </Row>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="icon"
                      onClick={() => handleRemoveItem(item.itemId)}
                      aria-label={`Remove ${item.itemId}`}
                    >
                      <Icon tokens={closeKnockoutTokens} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
        </Column>
      )}
    </Column>
  );
};

export default AddItemInput;
